import type { CRMUser, EmployeeDesignation, UserRole } from '../types';
import {
  hasFullCrmEmployeeAccess,
  hasOfficeManagerDesignation,
  hasSalesCrmAccess as hasSalesCrmAccessFromDesignations,
} from './employeeDesignations';

/** Internal LCTF designer. External project architects use {@link isArchitectRole}. */
export function isDesignerRole(role: string | undefined | null | unknown): boolean {
  const r = typeof role === 'string' ? role.trim().toLowerCase() : '';
  return r === 'designer';
}

/** External project architect (partner). Not CRM workspace staff. */
export function isArchitectRole(role: string | undefined | null | unknown): boolean {
  const r = typeof role === 'string' ? role.trim().toLowerCase() : '';
  return r === 'architect';
}

/** Invited trade-briefing subcontractor. Not CRM workspace staff. */
export function isSubcontractorRole(role: string | undefined | null | unknown): boolean {
  const r = typeof role === 'string' ? role.trim().toLowerCase() : '';
  return r === 'subcontractor';
}

export function designerRoleLabel(role: string | undefined | null): string {
  return isDesignerRole(role) ? 'Designer' : '';
}

/** Full CRM employee (Leads, Pipeline, workspace Settings) by role alone. */
export function isFullCrmEmployeeRole(role: UserRole | undefined): boolean {
  return role === 'admin' || role === 'employee';
}

/** Role or Office Manager designation — use for nav and route gates. */
export function isFullCrmEmployeeAccess(args: {
  authRole?: string | null;
  designations?: EmployeeDesignation[] | string[];
}): boolean {
  return hasFullCrmEmployeeAccess(args);
}

/** Project-focused internal roles — no Leads/Pipeline/sales Settings. */
export function isScopedInternalEmployeeRole(role: UserRole | undefined): boolean {
  return role === 'staff' || role === 'employee_shop' || role === 'employee_office';
}

/** W-2 employee tiers — 1099 contractor toggle disabled in Team edit. */
export function isW2EmployeeTierRole(role: UserRole | undefined): boolean {
  return role === 'employee' || role === 'employee_shop' || role === 'employee_office';
}

/**
 * Project folder "Financials" — admin, W-2 employee tiers, and Office Manager.
 * Hidden from field staff, designers, architects, engineers, suppliers, and clients.
 */
export function canSeeProjectFinancials(
  user:
    | { role?: UserRole | null; designations?: EmployeeDesignation[] | string[] }
    | null
    | undefined,
): boolean {
  const role = user?.role ?? undefined;
  if (role === 'admin' || isW2EmployeeTierRole(role)) return true;
  return hasOfficeManagerDesignation(user?.designations);
}

/** Team edit: selecting a W-2 employee tier clears 1099 before save. */
export function employeeRoleClears1099(role: UserRole | undefined): boolean {
  return isW2EmployeeTierRole(role);
}

/** Ops Hub / task assignees — full CRM employees plus scoped shop/office/staff. */
export function isOpsHubEligibleRole(role: UserRole | undefined): boolean {
  return role === 'admin' || role === 'employee' || isScopedInternalEmployeeRole(role);
}

/** Ops task assignees and internal action-required — not partners (designer, architect, engineer, supplier, …). */
export function isOpsTaskEmployeeRole(role: UserRole | undefined): boolean {
  return isOpsHubEligibleRole(role);
}

/** 1099 contractors — timesheets only; not W-2 ops/project assignees. */
export function is1099ContractorUser(
  user: Pick<CRMUser, 'is1099Contractor'> | null | undefined,
): boolean {
  return user?.is1099Contractor === true;
}

/**
 * Field / legacy-client 1099 logins — contractor shell only (not designer/architect/engineer 1099).
 */
export function isInternal1099ContractorUser(
  user:
    | { role?: UserRole | undefined; is1099Contractor?: boolean | undefined }
    | null
    | undefined,
): boolean {
  if (!is1099ContractorUser(user)) return false;
  const role = user?.role;
  if (
    role === 'admin'
    || role === 'employee'
    || role === 'employee_shop'
    || role === 'employee_office'
  ) {
    return false;
  }
  if (isDesignerRole(role) || isArchitectRole(role) || role === 'engineer') {
    return false;
  }
  return true;
}

/** Scoped shop/office/field W-2 routes — excludes 1099 contractors (they keep `staff` legacy role in Firestore). */
export function isW2ScopedInternalEmployee(
  user: Pick<CRMUser, 'role' | 'is1099Contractor'> | null | undefined,
): boolean {
  if (!user || is1099ContractorUser(user)) return false;
  return isScopedInternalEmployeeRole(user.role);
}

export function filterOpsTaskEmployeeTeam(members: CRMUser[]): CRMUser[] {
  return members.filter((u) => {
    if (!u.isActive) return false;
    // AI agent identities (Caesar) appear in Ops assignee / checklist pickers.
    if (u.isAgent === true) return true;
    return (
      isOpsTaskEmployeeRole(u.role)
      && !is1099ContractorUser(u)
      && !isCrmTestUser(u)
      && !isCrmWorkstationUser(u)
    );
  });
}

/** Finances → payroll position “Linked CRM user” — W-2 internal tiers only. */
export function isPayrollLinkableCrmUser(user: CRMUser): boolean {
  if (!user.isActive) return false;
  if (is1099ContractorUser(user)) return false;
  return user.role === 'admin' || isFullCrmEmployeeRole(user.role) || isScopedInternalEmployeeRole(user.role);
}

export function firstOpsTaskEmployeeUid(
  members: CRMUser[],
  uids: string[] | undefined,
): string | undefined {
  if (!uids?.length) return undefined;
  const eligible = new Set(filterOpsTaskEmployeeTeam(members).map((u) => u.uid));
  return uids.find((uid) => eligible.has(uid));
}

/** Sales CRM slice (Leads, Pipeline, proposals) — full Employee or `sales_rep` on a W-2 role. */
export function hasSalesCrmAccess(args: {
  authRole?: string | null;
  designations?: EmployeeDesignation[] | string[];
}): boolean {
  return hasSalesCrmAccessFromDesignations(args);
}

export function canAccessSalesCrmNav(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return hasSalesCrmAccessFromDesignations({ authRole: role, designations });
}

/** Analytics → Sales Activity (not UTM / Finances). */
export function canAccessSalesActivityNav(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return hasSalesCrmAccessFromDesignations({ authRole: role, designations });
}

/** Full analytics dropdown extras (UTM Builder). Finances stays on its own designation gates. */
export function canAccessAnalyticsNav(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return hasFullCrmEmployeeAccess({ authRole: role, designations });
}

/** Settings → Proposal Rates (full workspace Settings or the sales-rep slice). */
export function canAccessProposalRatesSettings(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return hasSalesCrmAccessFromDesignations({ authRole: role, designations });
}

export function canAccessWorkspaceSettings(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return hasFullCrmEmployeeAccess({ authRole: role, designations });
}

export function crmUserRoleLabel(role: string | undefined | null): string {
  const r = (role || '').trim().toLowerCase();
  if (isDesignerRole(r)) return 'Designer';
  if (isArchitectRole(r)) return 'Architect';
  if (r === 'employee') return 'Employee — Full';
  if (r === 'employee_shop') return 'Employee — Shop';
  if (r === 'employee_office') return 'Employee — Office';
  if (r === 'admin') return 'Admin';
  if (r === 'engineer') return 'Engineer';
  if (r === 'staff') return 'Field staff';
  if (r === 'supplier') return 'Supplier';
  if (r === 'cpa') return 'CPA';
  if (r === 'subcontractor') return 'Subcontractor';
  if (r === 'client') return 'Client';
  return role?.trim() || '';
}

/** Roles that can open `/project-hub` (all-projects list and per-project hub). */
export function canAccessProjectHub(role: UserRole | undefined): boolean {
  return (
    role === 'admin'
    || role === 'employee'
    || isScopedInternalEmployeeRole(role)
    || isDesignerRole(role)
    || isArchitectRole(role)
    || role === 'engineer'
    || role === 'supplier'
  );
}

/** Project Hub / files: same restrictions as designers and engineers (no create project, no values column, hide financial dashboard). */
export function isLimitedProjectViewer(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  if (hasOfficeManagerDesignation(designations)) return false;
  if (hasSalesCrmAccessFromDesignations({ authRole: role, designations })) return false;
  return (
    isDesignerRole(role)
    || isArchitectRole(role)
    || role === 'engineer'
    || isScopedInternalEmployeeRole(role)
  );
}

/** Designer, external architect, or engineer — no proposals, client portal, or financials. */
export function isDesignPartnerOrEngineerRole(role: UserRole | undefined): boolean {
  return isDesignerRole(role) || isArchitectRole(role) || role === 'engineer';
}

/** Client portal management + proposal builder — CRM employees and admins only. */
export function canAccessProjectHubClientPortalControls(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return hasSalesCrmAccessFromDesignations({ authRole: role, designations });
}

/** Same gate as client-portal management: proposal list/builder and proposal-rate settings. */
export function canAccessProposalAndClientPortal(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return canAccessProjectHubClientPortalControls(role, designations);
}

/**
 * Staff-only "Simulate under contract" planning overlay on Project Hub.
 * Employees and admins only — never designer / engineer / supplier / client portals.
 */
export function canSimulateUnderContract(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return hasFullCrmEmployeeAccess({ authRole: role, designations });
}

/** Staff inspiration board (Project Hub button, designer/architect/engineer portal shortcut, board route). */
export function canAccessInspirationBoard(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return (
    hasSalesCrmAccessFromDesignations({ authRole: role, designations })
    || isDesignerRole(role)
    || isArchitectRole(role)
    || role === 'engineer'
  );
}

/** Project Hub Suppliers tab — hidden from designer, architect, and engineer portals. */
export function canAccessProjectHubSuppliersTab(role: UserRole | undefined): boolean {
  return !isDesignerRole(role) && !isArchitectRole(role) && role !== 'engineer';
}

/**
 * Project Hub → Files → RFQs view (what the shops have, quote status, amounts) —
 * full CRM employees / Office Manager, or sales_rep on a W-2 role (same sales CRM
 * slice as Change Orders / proposals). Designers, engineers, scoped staff without
 * sales_rep, and partners never see it.
 */
export function canAccessProjectFilesRfqsView(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return hasSalesCrmAccessFromDesignations({ authRole: role, designations });
}

/**
 * Who may reshape the structure breakdown on Files: create proposal structures from
 * per-structure timber lists and move versions between tracks. Admins, PMs and full
 * employees — same gate as the RFQs view; partners and limited roles never see it.
 */
export function canManageProjectFileStructures(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return hasFullCrmEmployeeAccess({ authRole: role, designations });
}

/** Project Hub Change Orders tab — CRM employees and admins only. */
export function canAccessProjectHubChangeOrdersTab(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return hasSalesCrmAccessFromDesignations({ authRole: role, designations });
}

/**
 * Authenticated internal CRM user viewing the client portal or public proposal
 * as staff (not as the client). Designers/engineers must not skip the portal PIN
 * or get staff chrome on `/portal/<slug>` — they keep isCrmStaffUser for time tracking.
 */
export function isPortalStaffUser(role: UserRole | undefined): boolean {
  if (isDesignerRole(role) || role === 'engineer') return false;
  return isCrmStaffUser(role);
}

/** Project Hub task slide-over: full Firestore task activity audit — internal team only (not client / designer / engineer portals). */
export function canViewTaskActivityHistory(
  role: UserRole | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return (
    hasFullCrmEmployeeAccess({ authRole: role, designations })
    || isScopedInternalEmployeeRole(role)
  );
}

/** Admin-only action required wait-time pie chart in task detail panel. */
export function canViewActionRequiredWaitAnalytics(role: UserRole | undefined): boolean {
  return role === 'admin';
}

/** Settings user list — vendor portal accounts with supplierOrgId display as supplier even if Firestore role is stale. */
export function normalizeCrmUserDisplayRole(
  user: Pick<import('../types').CRMUser, 'role' | 'supplierOrgId'>,
): UserRole {
  if (user.role === 'supplier') return 'supplier';
  const orgId = user.supplierOrgId?.trim();
  if (orgId && user.role === 'client') return 'supplier';
  return user.role;
}

/**
 * Internal CRM users (not client portal, vendor, or external architect).
 * Includes designers/engineers for time tracking, activity hours, and Submittals.
 * Portal PIN skip / engagement staff chrome use isPortalStaffUser instead.
 */
export function isCrmStaffUser(role: UserRole | undefined): boolean {
  return (
    role === 'employee'
    || role === 'admin'
    || isScopedInternalEmployeeRole(role)
    || isDesignerRole(role)
    || role === 'engineer'
  );
}

/**
 * Project Hub collection-group reads of portalFileReleases (tokened download URLs).
 * Matches Firestore: isCrmWorkspaceUser && !isDesignPartnerOrEngineerRole.
 * CPA stays allowed; designers/engineers/architects and external roles are out.
 */
export function canReadPortalFileReleaseCollectionGroup(
  role: UserRole | undefined,
): boolean {
  if (!role) return false;
  if (isDesignPartnerOrEngineerRole(role)) return false;
  if (role === 'supplier' || role === 'client' || role === 'subcontractor') return false;
  return isCrmStaffUser(role) || isCpaUser(role);
}

/** Project floating tracker and per-project/task time panels — admins only. */
export function canViewProjectTimeResults(role: UserRole | undefined): boolean {
  return role === 'admin';
}

/** Log explicit hours on a Project Hub task — internal CRM team (not designer/engineer portals). */
export function canLogProjectTaskTime(role: UserRole | undefined): boolean {
  return role === 'employee' || role === 'admin' || isScopedInternalEmployeeRole(role);
}

/** Bonus and TN tax planners — admins and CPA portal (view / comment, not number edits). */
export function canViewBonusAndTaxPlans(role: UserRole | undefined): boolean {
  return role === 'admin' || role === 'cpa';
}

export function isCpaUser(role: UserRole | undefined): boolean {
  return role === 'cpa';
}

/**
 * Active internal people an admin may grant team chat to.
 * Test logins, workstations, 1099 contractors, and partner portals stay out.
 */
export function canGrantTeamChat(
  user: Pick<CRMUser, 'role' | 'isActive' | 'is1099Contractor' | 'isTestUser' | 'isWorkstation'> | null | undefined,
): boolean {
  if (!user || user.isActive === false) return false;
  if (user.is1099Contractor === true || user.isTestUser === true || user.isWorkstation === true) return false;
  const role = user.role;
  return role === 'admin'
    || role === 'employee'
    || role === 'employee_shop'
    || role === 'employee_office'
    || role === 'staff'
    || role === 'designer'
    || role === 'engineer';
}

/** Signed-in person may open /team-chat. The admin grant is required even for admins. */
export function canUseTeamChat(
  user: Pick<CRMUser, 'role' | 'isActive' | 'is1099Contractor' | 'isTestUser' | 'isWorkstation' | 'teamChatEnabled'> | null | undefined,
): boolean {
  return user?.teamChatEnabled === true && canGrantTeamChat(user);
}

/** QA / cutover logins grouped under Settings → Team → Test Users. */
export function isCrmTestUser(user: Pick<import('../types').CRMUser, 'isTestUser'> | null | undefined): boolean {
  return user?.isTestUser === true;
}

/** Shared machine login (Job Trailer, shop floor) — not a named teammate. */
export function isCrmWorkstationUser(
  user: Pick<import('../types').CRMUser, 'isWorkstation'> | null | undefined,
): boolean {
  return user?.isWorkstation === true;
}

/** Internal change order approval without client signature — admins only. */
export function canApproveChangeOrderInternally(role: UserRole | undefined): boolean {
  return role === 'admin';
}

/** Engineering markup suite (callouts, pattern lines, fastener stamps) on PDFs and images. */
export function canUseEngineeringMarkup(role: UserRole | undefined): boolean {
  return role === 'engineer' || role === 'admin' || role === 'employee';
}

/** Start / complete quiet plan reviews on Project Hub drawings. */
export function canManagePlanReview(role: UserRole | undefined): boolean {
  return (
    role === 'admin'
    || role === 'employee'
    || isScopedInternalEmployeeRole(role)
    || isDesignerRole(role)
  );
}

/** Project Hub “Move to Preconstruction” — admins and designated project managers only. */
export function canPromoteProjectToPreconstruction(args: {
  authRole?: UserRole;
  designations?: EmployeeDesignation[] | string[];
}): boolean {
  if (args.authRole === 'admin') return true;
  return args.authRole === 'employee' && !!args.designations?.includes('project_manager');
}

/** Version timeline: rename one upload’s display title (version label). Same gate as preconstruction promote. */
export function canEditProjectUploadVersionLabel(args: {
  authRole?: UserRole;
  designations?: EmployeeDesignation[] | string[];
}): boolean {
  return canPromoteProjectToPreconstruction(args);
}

/** Employees/admins may preview supplier or architect portals. */
export function canPreviewPartnerPortals(role: UserRole | undefined): boolean {
  return role === 'admin' || role === 'employee';
}

/** Firestore roles AuthContext may honor (unknown roles lock to client). */
export function isWhitelistedAuthRole(role: string | undefined | null): boolean {
  const r = (role || '').trim().toLowerCase();
  return (
    isDesignerRole(r)
    || isArchitectRole(r)
    || r === 'engineer'
    || r === 'admin'
    || r === 'employee'
    || r === 'employee_shop'
    || r === 'employee_office'
    || r === 'staff'
    || r === 'supplier'
    || r === 'cpa'
    || r === 'subcontractor'
  );
}
